import SyncReplaceable from '../SyncReplaceable'

/**
 * @param {string} name
 */
const getDefaultRegExp = (name) => {
  return new RegExp(`%%_RESTREAM_${name.toUpperCase()}_REPLACEMENT_(\\d+)_%%`, 'g')
}

/**
 * Make a unique string to replace cut text with.
 * @param {string} name
 * @param {number} index
 */
const getDefaultReplacement = (name, index) => {
  return `%%_RESTREAM_${name.toUpperCase()}_REPLACEMENT_${index}_%%`
}

/**
 * Create a new marker.
 * @param {string} name The name of the matcher, used in the doc marker.
 * @param {!RegExp} re The regular expression used for detection.
 * @param {!_restream.MakeMarkersConfig} [conf] Additional configuration.
 * @param {function(string, number): string} [conf.getReplacement] The function used to create a replacement when some text needs to be cut.
 * @param {function(string): !RegExp} [conf.getRegex] The function used to create a RegExp to detect replaced chunks.
 * @return {!_restream.Marker} The marker.
 */
const makeMarker = (name, re, conf) => {
  const {
    getReplacement = getDefaultReplacement,
    getRegex = getDefaultRegExp,
  } = conf || {}
  const regExp = getRegex(name)
  return {
    name,
    re,
    regExp,
    getReplacement,
    map: {},
    lastIndex: 0,
  }
}

/**
 * Make markers from a configuration object.
 * @param {!Object.<string, !RegExp>} matchers An object with types of markers to create as keys and their detection regexes as values.
 * @param {!_restream.MakeMarkersConfig} [config] Additional configuration.
 * @param {function(string, number): string} [config.getReplacement] The function used to create a replacement when some text needs to be cut.
 * @param {function(string): !RegExp} [config.getRegex] The function used to create a RegExp to detect replaced chunks.
 * @returns {!Object.<string, !_restream.Marker>} An object with markers for each requested type.
 */
export const makeMarkers = (matchers, config) => {
  const res = Object.keys(matchers).reduce((acc, key) => {
    const re = matchers[key]
    const marker = makeMarker(key, re, config)
    const m = {
      ...acc,
      [key]: marker,
    }
    return m
  }, {})
  return res
}

/**
 * Make a rule for pasting markers back.
 * @param {!_restream.Marker} marker A marker is used to cut and paste portions of text to exclude them from processing by other rules. Markers should be created using the `makeMarker` factory method that will assign their properties.
 * @param {!(_restream.Rule|Array<!_restream.Rule>)} [pipeRules] Any additional rules to replace the value of the marker before pasting it.
 * @returns {!_restream.Rule} A rule to paste previously replaced chunks.
 */
export const makePasteRule = (marker, pipeRules = []) => {
  const { regExp: re, map } = marker
  const rule = {
    re,
    replacement(match, index) {
      const m = map[index]
      delete map[index]
      const pr = Array.isArray(pipeRules) ? pipeRules : [pipeRules]
      const res = SyncReplaceable(m, pr)
      return res
    },
  }
  return rule
}

/**
 * Make a rule for initial replacement of markers.
 * @param {!_restream.Marker} marker A marker is used to cut and paste portions of text to exclude them from processing by other rules. Markers should be created using the `makeMarker` factory method that will assign their properties.
 * @returns {!_restream.Rule} A rule to cut matched chunks.
 */
export const makeCutRule = (marker) => {
  const { re, map, getReplacement, name } = marker
  const rule = {
    re,
    replacement(match) {
      const { lastIndex } = marker
      map[lastIndex] = match
      marker.lastIndex += 1
      const m = getReplacement(name, lastIndex)
      return m
    },
  }
  return rule
}

/**
 * @suppress {nonStandardJsDocs}
 * @typedef {import('..').Rule} _restream.Rule
 */

/* documentary types/markers.xml */
/**
 * @suppress {nonStandardJsDocs}
 * @typedef {_restream.Marker} Marker A marker is used to cut and paste portions of text to exclude them from processing by other rules. Markers should be created using the `makeMarker` factory method that will assign their properties.
 */
/**
 * @suppress {nonStandardJsDocs}
 * @typedef {Object} _restream.Marker A marker is used to cut and paste portions of text to exclude them from processing by other rules. Markers should be created using the `makeMarker` factory method that will assign their properties.
 * @prop {string} name The name of the marker for annotation purposes.
 * @prop {function(string, number): string} getReplacement The function to generate marker placeholders which can be then found, e.g., for (name: `marker`, index: `10`) by default _Restream_ will generate `%%_RESTREAM_MARKER_REPLACEMENT_10_%%`, but can be overriden with this method.
 * @prop {!RegExp} re The regular expression used for detection of the match.
 * @prop {!RegExp} regExp The generated regular expression to replace the marker back to its original value.
 * @prop {!Object<number, string>} map The map which holds detected matches at their indexes.
 * @prop {number} lastIndex The index of the last inserted element. Starts with 0.
 */
/**
 * @suppress {nonStandardJsDocs}
 * @typedef {_restream.MakeMarkersConfig} MakeMarkersConfig Additional configuration.
 */
/**
 * @suppress {nonStandardJsDocs}
 * @typedef {Object} _restream.MakeMarkersConfig Additional configuration.
 * @prop {function(string, number): string} [getReplacement] The function used to create a replacement when some text needs to be cut.
 * @prop {function(string): !RegExp} [getRegex] The function used to create a RegExp to detect replaced chunks.
 */
